import { ArrowRight, Clock } from 'lucide-react';

import ShareButton from '../access/ShareButton';
import {
  Chip,
  DetailShell,
  SectionLabel,
  StatItem,
  T,
  fmtDate,
  fmtDateTime,
  humanise,
  personLabel,
} from './shared';

const STATUS_TONE = {
  OPEN: { fg: T.info, bg: T.infoBg },
  FULFILLED: { fg: T.ok, bg: T.okBg },
  MET: { fg: T.ok, bg: T.okBg },
  STALLED: { fg: T.warn, bg: T.warnBg },
  MISSED: { fg: T.danger, bg: T.dangerBg },
  NEEDS_CONFIRMATION: { fg: T.textSec, bg: 'rgba(209,209,199,0.08)' },
};

const DAY_MS = 86400000;

/** "23 Aug · overdue 3d" — only for a deadline that's actually passed
 * on a commitment that isn't already closed out. */
const dueLabel = (commitment) => {
  const base = fmtDate(commitment.deadline_date);
  if (!commitment.deadline_date) return base;
  const closed = ['FULFILLED', 'MET'].includes(commitment.status);
  const overdueDays = Math.floor(
    (Date.now() - new Date(commitment.deadline_date).getTime()) / DAY_MS,
  );
  if (closed || overdueDays <= 0) return base;
  return `${base} · overdue ${overdueDays}d`;
};

/**
 * The detail drawer content for one commitment. `detail` is
 * `{ commitment: {...} }`. Same header/body/footer shape as
 * `RiskDetailPanel` via `DetailShell` — commitments have no write endpoint
 * yet, so the footer is a plain note instead of action buttons.
 *
 * Field names here follow the real Stage 4b/5 commitment shape
 * (`provenance`, `commitment_class`, `tracker`, `related_concern`, ...)
 * rather than a simplified frontend-only guess — Pydantic's
 * `extra = "ignore"` on the backend schema means these extra fields ride
 * along on the actual record even though the declared model only lists a
 * handful of them. `likelihood_basis` is deliberately not read here: it's
 * in `access.NEVER_PROJECTED`, so the API strips it from every response
 * regardless of what Stage 4b/5 wrote — showing it would mean showing
 * `undefined` forever.
 */
const CommitmentDetailPanel = ({ detail, onClose }) => {
  if (!detail?.commitment) return null;
  const commitment = detail.commitment;
  const assignment = commitment.assignment;
  const tone = STATUS_TONE[commitment.status] || {
    fg: T.textSec,
    bg: 'rgba(209,209,199,0.08)',
  };

  const followupLog = commitment.tracker?.followup_log || [];
  const deliverableRef = commitment.linked_entity_refs?.[0]?.ref;
  const relatedConcern = commitment.related_concern;

  return (
    <DetailShell
      chips={
        <>
          {commitment.commitment_class && (
            <Chip bordered>{humanise(commitment.commitment_class)}</Chip>
          )}
          {commitment.origin_meeting_type && (
            <Chip fg={T.muted} bordered>
              {humanise(commitment.origin_meeting_type)}
            </Chip>
          )}
        </>
      }
      statusChip={
        <Chip fg={tone.fg} bg={tone.bg}>
          {humanise(commitment.status)}
        </Chip>
      }
      title={commitment.normalised_text || 'Commitment'}
      raisedIn={
        commitment.provenance?.occurred_at
          ? `${humanise(commitment.provenance.source_channel) || 'a meeting transcript'} · ${fmtDateTime(commitment.provenance.occurred_at)}`
          : 'A meeting transcript'
      }
      quote={commitment.provenance?.quote}
      onClose={onClose}
      footer={
        <ShareButton
          itemType="commitment"
          itemId={commitment.entity_id}
          title={commitment.normalised_text}
        />
      }
    >
      {commitment.condition_text && (
        <div className="mb-3">
          <SectionLabel>Condition</SectionLabel>
          <p className="text-[13px]" style={{ color: T.textSec }}>
            {commitment.condition_text}
          </p>
        </div>
      )}

      {/* One shared border/bg for the whole stat block instead of nine
          individually-bordered fields — same treatment as the Insights
          detail panel's Status/Readiness/Owner/... grid. */}
      <div className="shadow-xs mb-3 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 sm:grid-cols-3">
        <StatItem
          label="Owner"
          value={personLabel(commitment.committer_person_id)}
        />
        <StatItem label="Deliverable" value={deliverableRef} />
        <StatItem label="Due" value={dueLabel(commitment)} />
        <StatItem
          label="Deadline type"
          value={
            [
              commitment.deadline_type && humanise(commitment.deadline_type),
              commitment.firmness && humanise(commitment.firmness),
            ]
              .filter(Boolean)
              .join(', ') || null
          }
        />
        <StatItem
          label="Likelihood"
          value={
            commitment.likelihood != null
              ? `${Math.round(commitment.likelihood * 100)}%`
              : null
          }
        />
        <StatItem
          label="Impact if missed"
          value={
            commitment.impact_of_non_fulfilment || commitment.impact_severity
              ? [
                  commitment.impact_of_non_fulfilment,
                  commitment.impact_severity &&
                    humanise(commitment.impact_severity),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : null
          }
        />
        <StatItem label="Role weight" value={commitment.role_tag} />
        <StatItem
          label="Revision"
          value={
            commitment.revision_kind ? humanise(commitment.revision_kind) : null
          }
        />
        <StatItem
          label="Restated"
          value={
            commitment.restatement_count != null
              ? `${commitment.restatement_count}×`
              : null
          }
        />
      </div>

      {assignment && (
        <div className="mb-3">
          <SectionLabel>Assignment</SectionLabel>
          <div className="shadow-xs grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 sm:grid-cols-3">
            <StatItem
              label="Acceptance"
              value={
                assignment.acceptance_status
                  ? humanise(assignment.acceptance_status)
                  : null
              }
            />
            <StatItem
              label="Estimate"
              value={
                assignment.estimate_points != null
                  ? `${assignment.estimate_points} pts${assignment.estimate_confidence ? ` · ${humanise(assignment.estimate_confidence)} confidence` : ''}`
                  : null
              }
            />
            <StatItem
              label="Progress"
              value={
                assignment.progress_pct != null
                  ? `${assignment.progress_pct}%`
                  : null
              }
            />
            <StatItem
              label="Definition of done"
              value={
                assignment.dod_completeness
                  ? humanise(assignment.dod_completeness)
                  : null
              }
            />
          </div>
        </div>
      )}

      {(relatedConcern || commitment.related_concern_ref) && (
        <div className="mb-3">
          <SectionLabel>Concerns</SectionLabel>
          <div
            className="shadow-xs flex items-center justify-between gap-3 rounded-xl border p-2.5"
            style={{ borderColor: T.br, background: 'rgba(255,255,255,0.02)' }}
          >
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-zinc-100">
                {relatedConcern?.description || commitment.related_concern_ref}
              </p>
              {relatedConcern && (
                <p className="mt-0.5 text-[11px]" style={{ color: T.muted }}>
                  {relatedConcern._id || commitment.related_concern_ref}
                  {relatedConcern.subtype &&
                    ` · ${relatedConcern.subtype.toLowerCase()}`}
                  {relatedConcern.status &&
                    ` · ${relatedConcern.status.toLowerCase()}`}
                </p>
              )}
            </div>
            <ArrowRight
              className="h-4 w-4 shrink-0"
              style={{ color: T.muted }}
            />
          </div>
        </div>
      )}

      <div className="mb-3">
        <SectionLabel>Followup</SectionLabel>
        {commitment.channel_expectation?.primary_followup_channel && (
          <p className="mb-1.5 text-[13px]" style={{ color: T.textSec }}>
            Watched via{' '}
            {humanise(commitment.channel_expectation.primary_followup_channel)}
            {commitment.channel_expectation.watch_window_days != null &&
              ` · ${commitment.channel_expectation.watch_window_days}-day window`}
          </p>
        )}
        {followupLog.length > 0 ? (
          <ul className="space-y-1.5">
            {followupLog.map((entry, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[12px]"
                style={{ color: T.textSec }}
              >
                <Clock
                  className="mt-0.5 h-3 w-3 shrink-0"
                  style={{ color: T.muted }}
                />
                <span>
                  {fmtDateTime(entry.at)} · {humanise(entry.channel)} sent ·{' '}
                  {humanise(entry.outcome)}
                  {entry.reply_text && (
                    <span className="block" style={{ color: T.text }}>
                      &ldquo;{entry.reply_text}&rdquo;
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px]" style={{ color: T.muted }}>
            No followup sent yet
          </p>
        )}
      </div>

      {commitment.linked_entity_refs?.length > 0 && (
        <div className="mb-3">
          <SectionLabel>Linked tickets</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {commitment.linked_entity_refs.map((link) => (
              <Chip key={link.ref} bordered>
                {link.ref} · {link.allocation_pct}%
              </Chip>
            ))}
          </div>
        </div>
      )}
    </DetailShell>
  );
};

export default CommitmentDetailPanel;
